import { useCallback, useEffect, useReducer, useMemo } from 'react';
import { useStorageState } from '../useStorageState';
import { useVessel } from '../../contexts/VesselContext';
import { devLog } from '../../utils/devLog';
import { readJSON } from '../../lib/storage';

/**
 * FT-48: Domain hook for maintenance jobs (Manual Maintenance Architecture)
 * Owns: maintenance_jobs_${vesselId} localStorage key
 *
 * This hook follows Domain Spine v2 pattern - domain logic separated from UI.
 * Presentational hooks (useUpcomingEvents, useDashboardCategories) compose this.
 *
 * IMPORTANT: This hook reads from maintenance_jobs (NOT maintenance_schedule)
 * to align with PlannedMaintenanceTab and AddJobView storage patterns.
 *
 * FT-92 FIX: Changed from static useMemo+localStorage.getItem to reactive useStorageState.
 * This ensures the dashboard sees maintenance jobs when they change.
 */
export const useMaintenanceSchedule = () => {
  // FT-92 FIX: Use useStorageState for reactive updates (same as useDrillsData, useDefects, etc.)
  // Base key only - vessel scoping handled by useStorageState
  const { value: storageSchedule = [], setValue: setScheduleRaw, loaded } = useStorageState('maintenance_jobs', []);

  // FT-92 FIX: Get current vessel for direct storage reads (fallback path)
  const { currentVessel } = useVessel();
  const vesselId = currentVessel?.id || 'default';

  // FT-92 FIX: Force re-render counter for when sr:jobs:updated fires
  const [updateCount, forceUpdate] = useReducer(x => x + 1, 0);

  // FT-92 FIX: Direct localStorage read as fallback
  // When useStorageState returns empty but localStorage has data, use localStorage directly
  // This handles cases where the event-based reactivity hasn't triggered yet
  //
  // FT-184 FIX: Filter out archived jobs - they should not appear on dashboard/calendar
  const schedule = useMemo(() => {
    let rawData;

    // If useStorageState has data, use it
    if (storageSchedule && storageSchedule.length > 0) {
      rawData = storageSchedule;
    } else {
      // Fallback: Read directly from localStorage using same key pattern
      // This matches how PlannedMaintenanceTab reads: storage.readJSON(`maintenance_jobs_${vesselId}`, [])
      const directKey = `maintenance_jobs_${vesselId}`;
      const directData = readJSON(directKey, []);

      if (directData && directData.length > 0) {
        devLog('[useMaintenanceSchedule] FT-92 FALLBACK: useStorageState empty but localStorage has', directData.length, 'jobs');
        rawData = directData;
      } else {
        rawData = storageSchedule;
      }
    }

    // FT-184 FIX: Filter out archived jobs (soft-deleted)
    // Matches useMaintenanceJobsDomain.isActive pattern
    const activeJobs = (rawData || []).filter(job => {
      const status = (job.status || 'active').toLowerCase();
      return status !== 'archived';
    });

    return activeJobs;
  }, [storageSchedule, vesselId, updateCount]); // updateCount triggers re-read when sr:jobs:updated fires

  // FT-92 DEBUG: Log what we're reading from storage
  useEffect(() => {
    if (loaded) {
      devLog('[useMaintenanceSchedule] Loaded', schedule?.length || 0, 'jobs:',
        schedule?.slice?.(0, 3)?.map?.(j => j.title || j.item) || []);
    }
  }, [loaded, schedule]);

  // FT-92 FIX: Listen for sr:jobs:updated event from useMaintenanceQuickActions
  // This ensures dashboard updates when jobs are saved via the direct storage.writeJSON path
  useEffect(() => {
    const handleJobsUpdated = (event) => {
      const eventVesselId = event.detail?.vesselId;
      if (eventVesselId && eventVesselId !== vesselId) {
        return; // Not our vessel
      }

      devLog('[useMaintenanceSchedule] sr:jobs:updated received, triggering re-read');

      // Trigger re-render which will re-run useMemo and potentially hit fallback path
      forceUpdate();
    };

    window.addEventListener('sr:jobs:updated', handleJobsUpdated);
    return () => window.removeEventListener('sr:jobs:updated', handleJobsUpdated);
  }, [vesselId]);

  /**
   * Set schedule - saves to localStorage via useStorageState
   * FT-92 FIX: Now uses setScheduleRaw from useStorageState which handles
   * vessel scoping and event dispatch automatically
   */
  const setSchedule = useCallback((newSchedule) => {
    setScheduleRaw(newSchedule);
  }, [setScheduleRaw]);

  /**
   * Get upcoming jobs within specified days
   */
  const getUpcoming = useCallback((daysAhead = 90) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const future = new Date(today);
    future.setDate(future.getDate() + daysAhead);

    return schedule.filter(job => {
      if (!job.nextDue) return false;
      const nextDue = new Date(job.nextDue);
      return nextDue >= today && nextDue <= future;
    });
  }, [schedule]);

  /**
   * Get overdue jobs
   */
  const getOverdue = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return schedule.filter(job => {
      if (!job.nextDue) return false;
      const nextDue = new Date(job.nextDue);
      return nextDue < today;
    });
  }, [schedule]);

  /**
   * Get job by ID
   */
  const getJobById = useCallback((id) => {
    return schedule.find(job => job.id === id) || null;
  }, [schedule]);

  return {
    schedule,
    setSchedule,
    getUpcoming,
    getOverdue,
    getJobById,
    // FT-92 FIX: Expose loaded state so consumers can wait for data
    loaded
  };
};
